import {ContentContainer} from '@coolcolduk/ui-mui';
import {Box, Button, FormControlLabel, MenuItem, Paper, Select, Switch, TextField, Typography} from '@mui/material';
import {Save, Add} from '@mui/icons-material';
import {useState, useMemo} from 'react';
import ErrorComponent from '../../components/ErrorComponent/ErrorComponent';
import PageHeader from '../../components/PageHeader';
import {PrivateRouteProps} from '../../config/routeType';
import useTodos from '../../hooks/todo/useTodos';
import useSavedSearches from '../../hooks/todo/useSavedSearches';
import useUntickExpiredTodos from '../../hooks/todo/useUntickExpiredTodos';
import LoadingPage from '../other/LoadingPage';
import TodoList from './TodoList';
import TodoForm from './TodoForm';

export default function TodoPage({user}: PrivateRouteProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [showDone, setShowDone] = useState(true);
  const [showArchive, setShowArchive] = useState(false);
  const [savedSearchId, setSavedSearchId] = useState<string>('');
  const [showForm, setShowForm] = useState(false);
  const [editingTodo, setEditingTodo] = useState<string | null>(null);

  const {savedSearches, saveSearch, loadSearch} = useSavedSearches(user?.id);
  const {todos, allTodos, loading, error} = useTodos(user?.id, {
    searchQuery,
    showDone,
    showArchive,
  });
  const {untickExpiredTodos, loading: unticking} = useUntickExpiredTodos(user?.id);

  const expiredDoneTodos = useMemo(() => todos.filter((todo) => todo.isExpired && todo.done !== null), [todos]);

  const hasNoTodos = !loading && allTodos.length === 0;

  const handleSaveSearch = () => {
    if (!searchQuery.trim()) return;

    const name = prompt('Enter a name for this search:');
    if (name) {
      saveSearch(name, searchQuery);
      setSavedSearchId('');
    }
  };

  const handleLoadSavedSearch = (searchId: string) => {
    if (!searchId) {
      setSearchQuery('');
      setSavedSearchId('');
      return;
    }

    const query = loadSearch(searchId);
    if (query) {
      setSearchQuery(query);
      setSavedSearchId(searchId);
    }
  };

  const handleUntickExpired = async () => {
    if (expiredDoneTodos.length === 0) return;

    try {
      await untickExpiredTodos(expiredDoneTodos.map((t) => t.id));
    } catch (err) {
      console.error('Failed to untick expired todos:', err);
    }
  };

  if (loading) {
    return <LoadingPage center minusAppBar />;
  }

  // If there are no todos, show empty state even if there's an error
  // (error might be due to collection not existing yet, which is fine)
  if (error && allTodos.length > 0) {
    return (
      <ErrorComponent
        message="Failed to load todos. Please try again."
        buttonText="Try again"
        handleClick={() => window.location.reload()}
      />
    );
  }

  return (
    <ContentContainer data-testid="todo-page">
      <PageHeader title="Todos" subtitle="Manage your todos and tasks" />

      {hasNoTodos ? (
        <Paper sx={{p: 4, textAlign: 'center'}}>
          <Typography variant="h6" gutterBottom>
            No todos yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{mb: 3}}>
            Get started by creating your first todo item
          </Typography>
          <Button variant="contained" startIcon={<Add />} onClick={() => setShowForm(true)}>
            Create Your First Todo
          </Button>
        </Paper>
      ) : (
        <>
          <Paper sx={{p: 2, mb: 3}}>
            <Box sx={{display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mb: 2}}>
              <TextField
                fullWidth
                placeholder="Search by title, note, or tags..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setSavedSearchId('');
                }}
                sx={{flex: 1, minWidth: 200}}
              />
              <Button variant="outlined" startIcon={<Save />} onClick={handleSaveSearch} disabled={!searchQuery.trim()}>
                Save Search
              </Button>
              {savedSearches.length > 0 && (
                <Select
                  value={savedSearchId}
                  onChange={(e) => handleLoadSavedSearch(e.target.value)}
                  displayEmpty
                  sx={{minWidth: 200}}
                >
                  <MenuItem value="">Load Saved Search</MenuItem>
                  {savedSearches.map((search) => (
                    <MenuItem key={search.id} value={search.id}>
                      {search.name}
                    </MenuItem>
                  ))}
                </Select>
              )}
            </Box>

            <Box sx={{display: 'flex', gap: 3, alignItems: 'center', flexWrap: 'wrap'}}>
              <FormControlLabel
                control={<Switch checked={showDone} onChange={(e) => setShowDone(e.target.checked)} />}
                label="Show Done"
              />
              <FormControlLabel
                control={<Switch checked={showArchive} onChange={(e) => setShowArchive(e.target.checked)} />}
                label="Show Archive"
              />
              {expiredDoneTodos.length > 0 && (
                <Button
                  variant="outlined"
                  color="warning"
                  onClick={handleUntickExpired}
                  disabled={unticking}
                  sx={{ml: 'auto'}}
                >
                  Untick Expired ({expiredDoneTodos.length})
                </Button>
              )}
              <Button variant="contained" onClick={() => setShowForm(true)} sx={{ml: 'auto'}}>
                Add Todo
              </Button>
            </Box>
          </Paper>

          <TodoList
            todos={todos}
            onEdit={(id) => {
              setEditingTodo(id);
              setShowForm(true);
            }}
            uid={user?.id}
          />
        </>
      )}

      {showForm && (
        <TodoForm
          open={showForm}
          todoId={editingTodo}
          onClose={() => {
            setShowForm(false);
            setEditingTodo(null);
          }}
          uid={user?.id}
        />
      )}
    </ContentContainer>
  );
}
